import { Box, BoxProps, Typography } from '@mui/material';
import {
  Control,
  FieldPath,
  FieldValues,
  useController,
} from 'react-hook-form';

import { useTranslate } from '@/hooks/useTranslate';

import { EmploymentStatus } from '@/types/user';

import { getRiskSectorOptions } from '@/utils/employment';

import { Select } from '../Select';

interface EmploymentRiskSectorSelectionProps<T extends FieldValues> {
  control: Control<T>;
  name: FieldPath<T>;
  employmentStatus?: EmploymentStatus;
  sx?: BoxProps['sx'];
}

export const EmploymentRiskSectorSelection = <T extends FieldValues>({
  control,
  name,
  employmentStatus = EmploymentStatus.Employed,
  sx,
}: EmploymentRiskSectorSelectionProps<T>) => {
  const { t } = useTranslate();
  const {
    fieldState: { error },
  } = useController({
    control,
    name,
  });

  return (
    <Box sx={sx} className="employment-risk-sector-selection">
      <Typography variant="caption" className="label" marginBottom={1}>
        {t(
          `employmentForm.form.employmentRiskSector.${employmentStatus}.question.label`,
        )}
      </Typography>
      <Box className="select-container">
        <Select
          options={getRiskSectorOptions().map(option => ({
            ...option,
            text: t(option.text),
            group: t(option.group),
          }))}
          control={control}
          name={name}
          error={!!error}
          helperText={error?.message}
          rules={{
            required: t(
              'employerForm.form.employmentRiskSector.categorySelection.required',
            ),
          }}
          fullWidth
          placeholder={t(
            'employerForm.form.employmentRiskSector.categorySelection.label',
          )}
          label={t(
            'employerForm.form.employmentRiskSector.categorySelection.label',
          )}
          useDrawerOnMobile
          withSearchBar={false}
          drawerTitle={
            <Typography variant="h4">
              {t(
                `employmentForm.form.employmentRiskSector.${employmentStatus}.question.label`,
              )}
            </Typography>
          }
          drawerHeaderLayoutWidths={[false, 12, false]}
          drawerOptionContainerSx={{
            padding: theme => theme.spacing(1, 2),
            '& .group-name': theme => ({
              borderTop: `1px solid ${theme.palette.neutral[200]}`,
              ...theme.typography.body2,
              fontWeight: theme.typography.fontWeightBold,
              paddingTop: theme.spacing(1.5),
              paddingX: 0,
            }),
            '& .group > li': theme => ({
              ...theme.typography.body2,
              fontWeight: theme.typography.fontWeightRegular,
              paddingTop: theme.spacing(1.5),
              paddingBottom: theme.spacing(1.5),
              paddingLeft: theme.spacing(1.5),
              paddingRight: theme.spacing(1.5),
            }),
          }}
          sx={{
            '& .MuiInputBase-root': {
              minHeight: 44,
              height: 'auto !important',
            },
          }}
        />
      </Box>
    </Box>
  );
};
